import { PrimaryButton } from '@/styles/blocks';
import { Grid, LinearProgress, MenuItem, TextField } from '@material-ui/core';
import { Switch } from '@onward-delivery/design';
import { CheckCircleOutline } from '@material-ui/icons';
import React, { useContext, useMemo, useState } from 'react';
import NavResponsiveModal, { ModalContent, ModalHeader } from '../../Navigation/NavResponsiveModal';
import { Context } from '../store';
import { css } from '@emotion/react';
import SearchInput from '../../misc/SearchInput';
import { colors } from '@/styles';
import { TABS } from '../constants';
import Table from './Table';
import CreateManifestFields from './CreateManifestFields';
import ManifestColumn from './ManifestColumn';
import { useNavigate } from 'react-router-dom';
import { isOnlySingleShipperType } from '../utils';
import { DESIGN_SCOPE_CLASS } from '@onward-delivery/design/scope';

// Cap only — `fullWidth` sets the real width. A viewport-relative one (the old `92vw`) can't know
// about NavResponsiveModal's sidebar inset, so it always overflowed and scrolled horizontally.
const MODAL_MAX_WIDTH = 1500;

const CreateManifest = ({ onUpdated } = {}) => {
    const { state, loading, callbacks } = useContext(Context);
    const isUpdate = !!state.newManifest?.isUpdate;
    const [shipperFilter, setShipperFilter] = useState(null);
    const [filterByReceivingDate, setFilterByReceivingDate] = useState(false);
    // Remount key for the orders table: clears its checkbox state after
    // "Add to Manifest" and on manifest-type changes
    const [tableEpoch, setTableEpoch] = useState(0);
    const navigate = useNavigate();

    const direction =
        state.newManifestDirection || (state.newManifest?.isDraft ? 'OUTBOUND' : state.tab.value) || 'INBOUND';
    const directionTab = TABS[direction] || TABS.INBOUND;
    const isInbound = direction === 'INBOUND';
    const hasReceivingDate = !!state.newManifest?.receiving_date;
    // Single-shipper types (INBOUND) require picking a shipper before the order list is usable.
    // Driven by type only — before a type is picked the Table is already gated on type, so no
    // order selection can happen anyway.
    const isOnlySingleShipper = isOnlySingleShipperType(state.newManifest?.type);
    // Update mode locks the filter to the manifest's existing shipper (set in openUpdateManifest).
    // Only applies while the type is still single-shipper (e.g. type changed away mid-update).
    const lockedShipperName = isOnlySingleShipper ? state.newManifest?.lockedShipperName || null : null;
    const effectiveShipperFilter = lockedShipperName || shipperFilter;

    const detailsValid = useMemo(() => {
        return ['name', 'receiving_date', 'warehouse_id', 'type'].every((attr) => state.newManifest?.[attr]);
    }, [state.newManifest]);

    const hasAddedItems = useMemo(
        () => Object.values(state.newManifest?.items || {}).some((selected) => selected),
        [state.newManifest?.items]
    );

    const itemsValid = useMemo(() => {
        if (state.newManifest?.isDraft && state.newManifest?.draft_items?.length > 0) {
            const allDraftItemsMatched = Object.values(state.matchedDraftItems).every((matched) => matched);
            return hasAddedItems && allDraftItemsMatched;
        }

        return hasAddedItems;
    }, [hasAddedItems, state.newManifest, state.matchedDraftItems]);

    // Sourced from the picker rows, not raw orders, so the filter can't offer a shipper whose
    // only candidate orders have no pickable items.
    const orderShipperOptions = useMemo(() => {
        return [
            ...new Set(
                (state.generatedData || []).map((order) => order?.order_shipper?.business_name).filter((name) => !!name)
            ),
        ];
    }, [state.generatedData]);

    const onDeleteDraft = () => {
        const confirmDelete = window.confirm(
            'Are you sure you want to delete this manifest draft? This cannot be undone.'
        );
        if (confirmDelete) {
            callbacks.deleteDraftManifest({
                variables: {
                    manifest_id: state.newManifest?.manifest_id,
                },
            });
        }
    };

    const onClose = () => {
        setShipperFilter(null);
        setFilterByReceivingDate(false);
        callbacks.closeCreateManifest();
    };

    const onAddToManifest = () => {
        callbacks.addSelectedRowsToManifest();
        setTableEpoch((prev) => prev + 1);
    };

    const onTypeChange = (type) => {
        callbacks.changeManifestType(type);
        setShipperFilter(null);
        setFilterByReceivingDate(false);
        setTableEpoch((prev) => prev + 1);
    };

    const onCreate = () => {
        callbacks.createManifest().then((result) => {
            const manifestId = result?.data?.created?.returning[0]?.manifest_id;
            // On failure the store's onError resolves this promise with no data —
            // keep the modal (and the user's picks) so they can retry.
            if (!manifestId) return;
            onClose();
            if (isUpdate) {
                // Update mode (from the single-manifest page): stay put and refresh the detail view.
                onUpdated?.();
            } else {
                navigate(`/manifests/${manifestId}`);
            }
        });
    };

    return (
        <NavResponsiveModal
            fullWidth
            maxWidth={false}
            PaperProps={{ style: { maxWidth: MODAL_MAX_WIDTH } }}
            open={!!state.showCreateManifestModal}
            onClose={onClose}
        >
            <ModalHeader
                title={`${isUpdate ? 'Update' : 'Create'} ${isInbound ? 'Inbound' : 'Outbound'} Order Manifest`}
                onClose={onClose}
            />
            <ModalContent
                css={css`
                    padding-top: 8px;
                `}
            >
                <CreateManifestFields isInbound={isInbound} directionTab={directionTab} onTypeChange={onTypeChange} />
                {state.newManifest?.isDraft ? (
                    <Grid
                        container
                        direction="column"
                        css={css`
                            margin-bottom: 0.5rem;
                        `}
                    >
                        <Grid
                            container
                            alignItems="center"
                            css={css`
                                gap: 1rem;
                                padding: 0.25rem 0;
                                font-size: 0.875rem;
                                color: inherit;
                            `}
                        >
                            <Grid item>Reference Id: {state?.newManifest?.reference_id || 'N/A'}</Grid>
                        </Grid>
                        {state?.newManifest?.draft_items?.map((item, index) => (
                            <Grid
                                key={index}
                                container
                                spacing={2}
                                alignItems="center"
                                css={css`
                                    padding: 0.25rem 0;
                                    font-size: 0.875rem;
                                    color: ${state.matchedDraftItems[index] ? colors.greens.primary : 'inherit'};
                                `}
                            >
                                <Grid item xs={item.pallet_id ? 4 : 6}>
                                    <div>SKU: {item.sku}</div>
                                </Grid>
                                {item.pallet_id ? (
                                    <Grid item xs={4}>
                                        <div>Pallet ID: {item.pallet_id}</div>
                                    </Grid>
                                ) : null}
                                <Grid item xs={item.pallet_id ? 4 : 6}>
                                    <div>Requested Quantity: {item.quantity}</div>
                                </Grid>
                                {state.matchedDraftItems[index] && (
                                    <Grid
                                        item
                                        css={css`
                                            color: ${colors.greens.primary};
                                            display: flex;
                                            align-items: center;
                                        `}
                                    >
                                        <CheckCircleOutline fontSize="small" />
                                    </Grid>
                                )}
                            </Grid>
                        ))}
                    </Grid>
                ) : null}
                <Grid
                    container
                    direction="row"
                    css={css`
                        flex-wrap: nowrap;
                        height: calc(100vh - 400px);
                        min-height: 420px;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 6 1 0;
                            /* min-width: auto would let the table's intrinsic width push this
                               pane, and the dialog, past its max-width. */
                            min-width: 0;
                            flex-wrap: nowrap;
                            padding-right: 1rem;
                        `}
                    >
                        <Grid
                            container
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            wrap="nowrap"
                            css={css`
                                margin-bottom: 1.25rem;
                            `}
                        >
                            <span
                                css={css`
                                    font-weight: 700;
                                    font-size: 16px;
                                `}
                            >
                                Orders
                            </span>
                            {/* Inbound only — outbound has no Est. Received Date column, so this
                                would just empty the grid. */}
                            {isInbound && (
                                <label
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        gap: 10px;
                                        white-space: nowrap;
                                        cursor: ${hasReceivingDate ? 'pointer' : 'default'};
                                        opacity: ${hasReceivingDate ? 1 : 0.6};
                                    `}
                                >
                                    <span
                                        css={css`
                                            font-size: 14px;
                                        `}
                                    >
                                        Filter by Receiving Date
                                    </span>
                                    {/* Scope class must sit on an ancestor; `display: contents`
                                        keeps the Switch as the flex item. Don't wrap MUI bordered
                                        controls in this — the scoped reset strips their border. */}
                                    <div className={DESIGN_SCOPE_CLASS} style={{ display: 'contents' }}>
                                        <Switch
                                            checked={filterByReceivingDate}
                                            disabled={!hasReceivingDate}
                                            onCheckedChange={setFilterByReceivingDate}
                                        />
                                    </div>
                                </label>
                            )}
                        </Grid>
                        <Grid
                            container
                            direction="row"
                            css={css`
                                gap: 0.75rem;
                                flex-wrap: nowrap;
                                margin-bottom: 0.5rem;
                            `}
                        >
                            <Grid
                                item
                                css={css`
                                    flex: 2 1 0;
                                    min-width: 0;
                                `}
                            >
                                <SearchInput
                                    placeholder="Search orders"
                                    onChange={(search) => callbacks.setSearchOrders(search)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid
                                item
                                css={css`
                                    flex: 1 1 0;
                                    min-width: 0;
                                `}
                            >
                                <TextField
                                    select
                                    variant="outlined"
                                    label={isOnlySingleShipper ? 'Shipper (required)' : 'Shipper'}
                                    size="small"
                                    required={isOnlySingleShipper}
                                    // Lock after orders are added (create) so switching shippers mid-pick can't mix them;
                                    // removing added items re-enables it. Update mode stays locked via lockedShipperName.
                                    disabled={!!lockedShipperName || (isOnlySingleShipper && hasAddedItems)}
                                    value={effectiveShipperFilter || ''}
                                    onChange={(e) => setShipperFilter(e.target.value || null)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                >
                                    {isOnlySingleShipper ? null : <MenuItem value="">All</MenuItem>}
                                    {/* Locked shipper may not appear in orderShipperOptions (its orders are already manifested) */}
                                    {lockedShipperName && !orderShipperOptions.includes(lockedShipperName) ? (
                                        <MenuItem value={lockedShipperName}>{lockedShipperName}</MenuItem>
                                    ) : null}
                                    {orderShipperOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                        {loading.ordersLoading ? (
                            <LinearProgress
                                css={css`
                                    margin-bottom: 2px;
                                `}
                            />
                        ) : null}
                        <Grid
                            item
                            css={css`
                                overflow: hidden;
                                flex-grow: 1;
                                min-height: 250px;
                            `}
                        >
                            {!state.newManifest?.type ? (
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="center"
                                    css={css`
                                        height: 100%;
                                        color: ${colors.greys.secondary};
                                    `}
                                >
                                    Select a manifest type to load available orders
                                </Grid>
                            ) : isOnlySingleShipper && !effectiveShipperFilter ? (
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="center"
                                    css={css`
                                        height: 100%;
                                        color: ${colors.greys.secondary};
                                    `}
                                >
                                    Select a shipper to begin
                                </Grid>
                            ) : (
                                <Table
                                    key={tableEpoch}
                                    state={state}
                                    callbacks={callbacks}
                                    shipperFilter={effectiveShipperFilter}
                                    receivingDateFilter={
                                        filterByReceivingDate ? state.newManifest?.receiving_date : null
                                    }
                                />
                            )}
                        </Grid>
                        <Grid
                            container
                            justifyContent="center"
                            css={css`
                                padding-top: 0.75rem;
                            `}
                        >
                            <PrimaryButton
                                disabled={
                                    loading.createLoading ||
                                    !(state.selectedRows || []).length ||
                                    (isOnlySingleShipper && !effectiveShipperFilter)
                                }
                                onClick={onAddToManifest}
                            >
                                Add to Manifest
                            </PrimaryButton>
                        </Grid>
                    </Grid>
                    <ManifestColumn
                        onCreate={onCreate}
                        createDisabled={
                            loading.createLoading ||
                            !detailsValid ||
                            !itemsValid ||
                            (isOnlySingleShipper && !effectiveShipperFilter)
                        }
                        onDeleteDraft={onDeleteDraft}
                        isUpdate={isUpdate}
                    />
                </Grid>
            </ModalContent>
        </NavResponsiveModal>
    );
};

export default CreateManifest;
